import React, { useState, useEffect } from "react";
import axios from "axios";
import WorkflowDetails from "./WorkflowDetails";
import { useNavigate } from "react-router-dom";
import API_BASE from "./config"; 
import BYOP from "./byop";
import WorkflowDesigner from "./WorkflowDesigner";
import { usePermissions, RequirePermission } from "./hooks/usePermissions";

const Spinner = () => (
  <>
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
    <span
      style={{
        display: "inline-block",
        width: "16px",
        height: "16px",
        marginRight: "8px",
        border: "3px solid #e5e7eb",
        borderTop: "3px solid #2563eb",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }}
    />
  </>
);

const LandingPage = ({ userData }) => {
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState(""); // unified output
  const [conversation, setConversation] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [showChatPanel, setShowChatPanel] = useState(true);
  const [workflowStatus, setWorkflowStatus] = useState("");
  const [workflowMetadataId, setWorkflowMetadataId] = useState("");
  const [viewMode, setViewMode] = useState("DEFAULT");
  const { permissions } = usePermissions();
  const [mode, setMode] = useState("quick"); // "quick" or "detailed"
  const [modeLocked, setModeLocked] = useState(false);

  const animatedQuestions = [
    "I want to quantify gene expression in my RNASeq data...",
    "I want to identify vector integration sites in my WGS data...",
    "I want to perform metagenomics analysis on my soil sample fastq files...",
    "I want to detect structural variants in my long-read sequencing data...",
    "I want to annotate microbial genomes from assembled contigs..."
  ];

  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [userFocused, setUserFocused] = useState(false);

  const navigate = useNavigate();
  const [localFolderName, setLocalFolderName] = useState(null);

  const fetchChatHistory = async (chatId) => {
    try {
      const result = await axios.get(`${API_BASE}/chat-history/${chatId}`);
      const history = result.data.history.map((item) => ({
        role: item.role,
        content: item.message
      }));
      setConversation(history);
      setSelectedChatId(chatId);
      setViewMode("DEFAULT");

      const lastAssistant = history.filter((m) => m.role === "assistant").pop();
      setResponse(lastAssistant ? lastAssistant.content : "");
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  const createNewChat = async () => {
    try {
      const result = await axios.post(`${API_BASE}/create-chat`, { user_id: userData.userId });
      if (result.status === 200) {
        const newChatId = result.data.chat_id;
        setSelectedChatId(newChatId);
        setChats([...chats, { id: newChatId, created_on: new Date().toISOString() }]);
        setConversation([]);
        setQuestion("");
        setResponse("");
        setWorkflowStatus("");
        setViewMode("DEFAULT");
        setMode("quick");
        setModeLocked(false);
      } else {
        alert("Failed to create new chat.");
      }
    } catch (error) {
      console.error("Error creating new chat:", error);
    }
  };

  const fetchCognitoUserId = async () => {
    try {
      const idToken = localStorage.getItem("idToken");
      if (!idToken) throw new Error("Missing ID token");

      const response = await axios.post(
        `${API_BASE}/get-user-id`,
        {},
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      return response.data?.cognitoUserId || null;
    } catch (error) {
      console.error("Failed to fetch Cognito user ID:", error);
      return null;
    }
  };

    const askQuestion = async () => {
    try {
      const userQuestion = question;
      if (!modeLocked) setModeLocked(true);
      setQuestion(""); // clear immediately

      const updatedConversation = [...conversation, { role: "user", content: userQuestion }];
      setConversation(updatedConversation);

      // Reset unified response box
      setResponse("Thinking...");

      const cognitoUserId = await fetchCognitoUserId();

      const response = await fetch(`https://cloudgenomics.net/claude-workflow`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_id: userData.userId,
          chat_id: selectedChatId,
          cognito_user_id: cognitoUserId,
          messages: updatedConversation,
          mode: mode
        })
      });

      if (!response.ok) {
        setResponse("An error occurred. Please create a new chat and try again.");
        return;
      }

      // Detect PENDING JSON response
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const json = await response.json();

        if (json.status === "PENDING") {
          setWorkflowStatus("PENDING");

          // Append PENDING message to unified response box
          setResponse((prev) =>
            (prev === "Thinking..." ? "" : prev + "<br/>") + json.message
          );

          setConversation([
            ...updatedConversation,
            { role: "assistant", content: json.message }
          ]);

          return;
        }
      }

      // SSE streaming branch
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const processChunk = ({ done, value }) => {
        if (done) return;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const line = part.trim();
          if (!line) continue;

          const dataLine = line.startsWith("data:") ? line.slice(5).trim() : null;
          if (!dataLine) continue;

          try {
            const parsed = JSON.parse(dataLine);

            // Normal workflow log → append to unified response
            if (parsed.type !== "final" && parsed.message) {
              setResponse((prev) => prev + "<br/>" + parsed.message);
            }

            // Final Claude answer → append to unified response
            if (parsed.type === "final" && parsed.message) {
              setResponse((prev) => prev + "<br/><br/>" + parsed.message);
            }

            // Trigger redirect to WorkflowDetails
            if (parsed.type === "final") {
              setWorkflowStatus("COMPLETE");
              setWorkflowMetadataId(parsed.workflowMetadataId);
            }
          } catch (err) {
            console.error("Failed to parse SSE data:", err, dataLine);
          }
        }

        return reader.read().then(processChunk);
      };

      await reader.read().then(processChunk);
    } catch (error) {
      console.error("askQuestion error:", error);
      setResponse("An error occurred. Please create a new chat and try again.");
    }
  };

  useEffect(() => {
    if (!selectedChatId && !userFocused) {
      const currentText = animatedQuestions[placeholderIndex];
      if (charIndex < currentText.length) {
        const timeout = setTimeout(() => {
          setAnimatedPlaceholder((prev) => prev + currentText[charIndex]);
          setCharIndex((prev) => prev + 1);
        }, 12);
        return () => clearTimeout(timeout);
      } else {
        const pause = setTimeout(() => {
          setCharIndex(0);
          setAnimatedPlaceholder("");
          setPlaceholderIndex((prev) => (prev + 1) % animatedQuestions.length);
        }, 2000);
        return () => clearTimeout(pause);
      }
    }
  }, [charIndex, placeholderIndex, selectedChatId, userFocused]);

    return (
    <div style={{ display: "flex", height: "100vh", background: "#f1f5f9" }}>
      {showChatPanel && (
        <div
          style={{
            width: "300px",
            background: "linear-gradient(180deg, #232e47, #111733)",
            color: "#e5e7eb",
            padding: "16px",
            position: "relative"
          }}
        >
          <h4 style={{ marginBottom: "12px", color: "#cbd5f5" }}>
            Welcome, {userData.name}
            <br />
            Start Building!
          </h4>

          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            <RequirePermission
              permission="createBuild"
              fallback={<span style={{ color: "gray", alignSelf: "center", fontSize: "12px" }}>Create Builds (not allowed)</span>}
            >
              <button
                onClick={createNewChat}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: "none"
                }}
              >
                + New Build
              </button>
            </RequirePermission>

            <button
              onClick={() => setViewMode("DESIGN")}
              style={{
                flex: 1,
                padding: "10px",
                background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                color: "#fff",
                borderRadius: "10px",
                cursor: "pointer",
                border: "none"
              }}
            >
              Design
            </button>
          </div>

          <h3 style={{ marginBottom: "8px", color: "#cbd5f5" }}>Builds</h3>
          {chats.length > 0 ? (
            chats.map((chat, index) => (
              <div
                key={chat.id}
                onClick={() => fetchChatHistory(chat.id)}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  marginBottom: "6px",
                  background:
                    selectedChatId === chat.id ? "rgba(255,255,255,0.12)" : "transparent"
                }}
              >
                {`Build #${index + 1}`}
              </div>
            ))
          ) : (
            <p style={{ color: "#94a3b8" }}>Create a new build by clicking button above.</p>
          )}


          <div
            style={{
              position: "fixed",
              bottom: "20px",
              left: "10px",
              width: "280px",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <button
              onClick={() => navigate("/off-the-shelf")}
              style={{
                padding: "10px",
                background: "#2563eb",
                color: "#fff",
                borderRadius: "10px",
                cursor: "pointer",
                border: "none",
                width: "120px"
              }}
            >
              Off-the-Shelf
            </button>
            <button
              onClick={() => setViewMode("BYOP")}
              style={{
                padding: "10px",
                background: "#2563eb",
                color: "#fff",
                borderRadius: "10px",
                cursor: "pointer",
                border: "none",
                width: "120px"
              }}
            >
              Bring Your Own
            </button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, padding: "32px", display: "flex", flexDirection: "column" }}>
        {viewMode === "DESIGN" ? (
          <WorkflowDesigner userData={userData} onClose={() => setViewMode("DEFAULT")} />
        ) : viewMode === "BYOP" ? (
          <BYOP />
        ) : workflowStatus === "COMPLETE" ? (
          <WorkflowDetails
            chatId={selectedChatId}
            cognitoUserId={userData.cognitoUserId}
            workflowMetadataId={workflowMetadataId}
            organization={userData.organization}
            localFolderName={localFolderName}
          />
        ) : (
          <>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              
            }}>
              <h1 style={{ color: "#0f172a" }}>What do you want to build today?</h1>

              {/* Quick / Detailed Toggle */}
              <div style={{
                position: "relative",
                display: "flex",
                width: "130px",
                background: "#e2e8f0",
                borderRadius: "16px",
                border: "1px solid #9badc4",
                height: "26px",
              }}>
                {/* Sliding highlight */}
                <div
                  style={{
                    position: "absolute",
                    top: "3px",
                    left: mode === "quick" ? "3px" : "65px",
                    width: "62px",
                    height: "20px",
                    background: "#6785c5",
                    borderRadius: "12px",
                    transition: "left 0.25s ease",
                  }}
                />

                {/* Quick */}
                <div
                  onClick={() => !modeLocked && setMode("quick")}
                  style={{
                    width: "65px",
                    textAlign: "center",
                    zIndex: 2,
                    lineHeight: "26px",
                    cursor: modeLocked ? "not-allowed" : "pointer",
                    color: mode === "quick" ? "#fff" : "#1e293b",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  Quick
                </div>

                {/* Detailed */}
                <div
                  onClick={() => !modeLocked && setMode("detailed")}
                  style={{
                    width: "65px",
                    textAlign: "center",
                    zIndex: 2,
                    lineHeight: "26px",
                    cursor: modeLocked ? "not-allowed" : "pointer",
                    color: mode === "detailed" ? "#fff" : "#1e293b",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  Detailed
                </div>
              </div>


            </div>

            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  askQuestion();
                }
              }}
              onFocus={() => setUserFocused(true)}
              onBlur={() => setUserFocused(false)}
              placeholder={selectedChatId ? "Type your question here" : animatedPlaceholder}
              rows={2}
              disabled={!selectedChatId}
              style={{
                padding: "16px",
                borderRadius: "14px",
                border: "1px solid #cbd5e1",
                backgroundColor: !selectedChatId ? "#e5e7eb" : "#ffffff",
                cursor: !selectedChatId ? "not-allowed" : "text",
                fontSize: "15px",
                marginBottom: "12px"
              }}
            />

            <RequirePermission permission="createBuild">
              <button
                onClick={askQuestion}
                disabled={!selectedChatId || !question.trim()}
                style={{
                  alignSelf: "flex-end",
                  padding: "12px 24px",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(37,99,235,0.35)"
                }}
              >
                Ask
              </button>
            </RequirePermission>

            {/* Unified CloudGenomics Response Box */}
            <div
              style={{
                marginTop: "20px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column"
              }}
            >
              <strong style={{ marginBottom: "8px" }}>CloudGenomics Response:</strong>

              <div
                style={{
                  flexGrow: 1,
                  overflowY: "auto",
                  padding: "20px",
                  borderRadius: "16px",
                  background: "#ffffff",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  maxHeight: "500px"
                }}
              >
                {response === "Thinking..." ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Spinner />
                    <span>Thinking. Do not refresh or close this page...</span>
                  </div>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: response || "<p>Awaiting question!</p>"
                    }}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LandingPage;